import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

import type { FeatureFlagKey } from '@/types/featureFlags'
import { featureFlagKeys, featureFlagsApi } from '../api/featureFlagsApi'

/**
 * Hvilke moduler der er slået til for organisationen.
 *
 * <p>Læsning af flagene er åben for alle medlemmer — netop fordi frontenden
 * skal vide, hvad den må vise. Se <c>FeatureFlagsController</c>.</p>
 *
 * <p><strong>Dette er pynt, ikke sikkerhed.</strong> Backend spærrer uafhængigt
 * af, hvad der står her; hooken findes for at undgå en fane, der åbner et 403.
 * Bliver svaret ved med at være undervejs, antager vi <em>slået til</em>: en
 * fane, der forsvinder et øjeblik ved hver sideindlæsning, er værre end en, der
 * en sjælden gang viser en fejlbesked.</p>
 */
export function useModules() {
  const flags = useQuery({
    queryKey: featureFlagKeys.all,
    queryFn: featureFlagsApi.list,

    // Flagene ændres af en administrator nogle få gange i et produkts levetid.
    // At hente dem ved hver sideskift ville være et kald uden en læser.
    staleTime: 5 * 60 * 1000,
  })

  // Bunden til svaret og ikke til rendringen: kaldere filtrerer menupunkter med
  // den inde i en `useMemo`, og en ny funktion ved hver rendring ville gøre den
  // memo til ingenting.
  const isEnabled = useCallback(
    (key: FeatureFlagKey): boolean => {
      const flag = flags.data?.find((candidate) => candidate.key === key)

      // Se ovenfor: ukendt betyder "vis det", ikke "skjul det".
      return flag?.isEnabled ?? true
    },
    [flags.data],
  )

  return { isEnabled, isPending: flags.isPending }
}
